import type { FC, ReactNode, RefObject } from "react";
import { Fragment } from "react";

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

import Button from "~/atoms/buttons/Button";
import ButtonLoading from "~/atoms/buttons/ButtonLoading";

interface BaseDrawerProps {
  children: ReactNode;
  childrenOpen?: boolean;
  disabled?: boolean;
  onClose: () => void;
  open: boolean;
  initialFocus?: RefObject<HTMLElement>;
  parent?: boolean;
  nested?: ReactNode;
  position?: "left" | "right";
  size?: "sm" | "md";
}

/**
 * This is a base drawer component.
 *
 * @param {node} children - The content of the drawer.
 * @param {boolean} childrenOpen - Whether the child drawers are open.
 * @param {boolean} open - Whether the drawer is open.
 * @param {function} onClose - The function to call when the drawer is closed.
 * @param {string} initialFocus - The ref of the element to focus on when the drawer opens.
 * @param {boolean} parent - Whether the drawer is a parent drawer.
 */
export const BaseDrawer: FC<BaseDrawerProps> = ({
  children,
  childrenOpen = false,
  onClose,
  open,
  initialFocus,
  parent = false,
  nested = false,
  position = "right",
  size = "md",
}) => {
  const maxWidthClass = size === "sm" ? "max-w-sm" : "max-w-md";

  return (
    <Transition show={open} as={Fragment} appear>
      <Dialog
        as="div"
        className="relative z-60"
        onClose={parent && childrenOpen ? () => {} : onClose}
        initialFocus={initialFocus}
      >
        {parent && (
          <TransitionChild
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity" />
          </TransitionChild>
        )}

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div
              className={`pointer-events-none fixed inset-y-0 flex max-w-full ${
                position === "left" ? "left-0 sm:pr-10" : "right-0 sm:pl-10"
              }`}
            >
              <TransitionChild
                as={Fragment}
                enter="transform transition ease-in-out duration-500"
                enterFrom={
                  position === "left" ? "-translate-x-full" : "translate-x-full"
                }
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500"
                leaveFrom="translate-x-0"
                leaveTo={
                  position === "left" ? "-translate-x-full" : "translate-x-full"
                }
              >
                <DialogPanel
                  className={`pointer-events-auto w-screen ${maxWidthClass}`}
                >
                  <div className="pb-safe-bottom flex h-full flex-col overflow-y-scroll bg-white pt-[calc(env(safe-area-inset-top)+1.5rem)] shadow-xl">
                    {children}
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
        {nested && nested}
      </Dialog>
    </Transition>
  );
};

interface DrawerHeaderProps {
  children: ReactNode;
  onClose: () => void;
  title: string;
}
export const DrawerHeader: FC<DrawerHeaderProps> = ({
  children,
  onClose,
  title,
}) => (
  <>
    <div className="px-4 sm:px-6">
      <div className="flex items-start justify-between">
        <DialogTitle className="text-base leading-6 font-semibold text-gray-900">
          {title}
        </DialogTitle>
        <div className="ml-3 flex h-7 items-center">
          <button
            type="button"
            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden"
            onClick={onClose}
          >
            <span className="sr-only">Close drawer</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
    <div className="relative mt-6 flex-1 px-4 sm:px-6">{children}</div>
  </>
);

interface DrawerFooterCustomProps {
  closeLabel?: string;
  onClose?: () => void;
  submitLabel?: string;
  onSubmit?: () => void;
  loading: boolean;
  disabled?: boolean;
}

export const DrawerFooterOnlyPrimary = ({
  submitLabel = "Save",
  onSubmit,
  loading,
  disabled,
}: Omit<DrawerFooterCustomProps, "onClose">) => (
  <div className="flex gap-4 px-4 py-6 sm:px-6">
    <div className="flex-1">
      {typeof onSubmit === "function" ? (
        <ButtonLoading
          onClick={onSubmit}
          loading={loading}
          fullWidth
          disabled={disabled}
          type="submit"
        >
          {submitLabel}
        </ButtonLoading>
      ) : null}
    </div>
  </div>
);

export const DrawerFooterCustom: FC<DrawerFooterCustomProps> = ({
  closeLabel = "Close",
  submitLabel = "Save",
  onClose,
  onSubmit,
  loading,
  disabled,
}) => (
  <div className="flex gap-4 px-4 py-6 sm:px-6">
    <div className="flex-1">
      {typeof onClose === "function" ? (
        <Button onClick={onClose} variant="outlined" color="tertiary" fullWidth>
          {closeLabel}
        </Button>
      ) : null}
    </div>
    <div className="flex-1">
      {typeof onSubmit === "function" ? (
        <ButtonLoading
          onClick={onSubmit}
          loading={loading}
          fullWidth
          disabled={disabled}
          type="submit"
        >
          {submitLabel}
        </ButtonLoading>
      ) : null}
    </div>
  </div>
);

interface DrawerFooterProps extends Omit<DrawerFooterCustomProps, "onClose"> {
  closeLabel?: string;
  submitLabel?: string;
  onClose: () => void;
}

const DrawerFooter: FC<DrawerFooterProps> = ({
  closeLabel = "Close",
  disabled,
  loading,
  onClose,
  onSubmit,
  submitLabel = "Save",
}) => (
  <DrawerFooterCustom
    closeLabel={closeLabel}
    disabled={disabled}
    submitLabel={submitLabel}
    onClose={onClose}
    onSubmit={onSubmit}
    loading={loading}
  />
);

interface DrawerFilterFooterProps {
  onClose: () => void;
  onSubmit: () => void;
  loading: boolean;
}

const DrawerFilterFooter: FC<DrawerFilterFooterProps> = ({
  onClose,
  onSubmit,
  loading,
}) => (
  <div className="flex gap-4 p-6">
    <div className="flex-1">
      <Button onClick={onClose} variant="outlined" color="tertiary" fullWidth>
        Close
      </Button>
    </div>
    <div className="flex-1">
      <ButtonLoading onClick={onSubmit} loading={loading} fullWidth>
        Filter
      </ButtonLoading>
    </div>
  </div>
);

interface DrawerNoFooterProps extends BaseDrawerProps {
  title: string;
}

export const DrawerNoFooter: FC<DrawerNoFooterProps> = ({
  children,
  childrenOpen,
  onClose,
  open,
  title,
  initialFocus,
  parent,
  nested,
  position,
  size,
}) => (
  <BaseDrawer
    childrenOpen={childrenOpen}
    open={open}
    onClose={onClose}
    initialFocus={initialFocus}
    parent={parent}
    nested={nested}
    position={position}
    size={size}
  >
    <DrawerHeader onClose={onClose} title={title}>
      {children}
    </DrawerHeader>
  </BaseDrawer>
);

interface FilterDrawerProps extends BaseDrawerProps {
  onSubmit: () => void;
  title: string;
  loading: boolean;
}

/**
 * This is a drawer component with a title and buttons, and space for content.
 *
 * @param {node} children - The content of the drawer.
 * @param {boolean} childrenOpen - Whether the child drawers are open.
 * @param {function} onClose - The function to call when the drawer is closed.
 * @param {boolean} open - Whether the drawer is open.
 * @param {function} onSubmit - The function to call when the drawer is submitted.
 * @param {string} title - The title of the drawer.
 * @param {string} initialFocus - The ref of the element to focus on when the drawer opens.
 * @param {boolean} parent - Whether the drawer is a parent drawer.
 * @param {node} nested - The nested drawer.
 */
export const FilterDrawer: FC<FilterDrawerProps> = ({
  children,
  childrenOpen,
  onClose,
  open,
  onSubmit,
  title,
  initialFocus,
  parent,
  nested,
  loading,
  position,
  size,
}) => (
  <BaseDrawer
    childrenOpen={childrenOpen}
    open={open}
    onClose={onClose}
    initialFocus={initialFocus}
    parent={parent}
    nested={nested}
    position={position}
    size={size}
  >
    <DrawerHeader onClose={onClose} title={title}>
      {children}
    </DrawerHeader>
    <DrawerFilterFooter
      onClose={onClose}
      onSubmit={onSubmit}
      loading={loading}
    />
  </BaseDrawer>
);
interface DrawerProps
  extends Omit<BaseDrawerProps, "onClose">, DrawerFooterProps {
  title: string;
}

/**
 * This is a drawer component with a title and buttons, and space for content.
 *
 * @param {node} children - The content of the drawer.
 * @param {boolean} childrenOpen - Whether the child drawers are open.
 * @param {boolean} loading - Whether the drawer is loading - passed to button.
 * @param {function} onClose - The function to call when the drawer is closed.
 * @param {boolean} open - Whether the drawer is open.
 * @param {function} onSubmit - The function to call when the drawer is submitted.
 * @param {string} title - The title of the drawer.
 * @param {string} initialFocus - The ref of the element to focus on when the drawer opens.
 * @param {boolean} parent - Whether the drawer is a parent drawer.
 * @param {node} nested - The nested drawer.
 */
const Drawer: FC<DrawerProps> = ({
  children,
  childrenOpen,
  closeLabel,
  disabled,
  loading,
  onClose,
  open,
  onSubmit,
  title,
  submitLabel,
  initialFocus,
  parent,
  nested,
  position,
  size,
}) => (
  <BaseDrawer
    childrenOpen={childrenOpen}
    open={open}
    onClose={onClose}
    initialFocus={initialFocus}
    parent={parent}
    nested={nested}
    position={position}
    size={size}
  >
    <DrawerHeader onClose={onClose} title={title}>
      {children}
    </DrawerHeader>
    <DrawerFooter
      closeLabel={closeLabel}
      disabled={disabled}
      loading={loading}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={submitLabel}
    />
  </BaseDrawer>
);

export default Drawer;
